import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context';
import loadable from '@loadable/component';
import { Panel } from '@prisjakt/ui/panel';
import { ScrollElement } from 'src/components/scroll';
import { getCurrentCampaign, fetchDeals } from './utils';
import AdContainer from '../../components/ads';
import Lazy from '@prisjakt/ui-lazy';
import { PageContent, PageSection } from 'src/components/page';
import RecentlyVisitedProducts from '../../components/recently-visited';
import { PRODUCT_ALERT_MODAL_ID } from './constants';
import { CurrentCampaign, ProductPageProps } from './types';
import { setSelectedCondition } from './actions/product';
import { ProductPageHeader } from './components/product-header';
import { ProductTabs } from './components/product-tabs';
import ProductDuringPromoBanner from './components/product-promo-banner/product-during-promo-banner';
import { ProductBreadCrumb } from './components/product-breadcrumb';
import ProductBeforePromoBanner from './components/product-promo-banner/product-before-promo-banner';
import ProductScrollTopButton from './components/product-scroll-top-button';
import ProductCampaignDeals from './components/product-campaign-deals';
import { useEffectOnMount } from '../../hooks/useEffectOnMount';
import { ProductNavigation } from './components/product-section-navigation';
import { MobileTopAd } from './components/price-list/ads/mobile-top-ad';
import { ProductPage as StyledProductPage, Modal } from './styles';

const ProductAlertModalContent = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../product-alert/components/product-alert-modal-content'
  )
);

export const ProductPage = ({
  activeCampaign,
  data,
  routeSection = 'main',
  campaignBanners,
  currentModal,
  hideModal,
}: ProductPageProps) => {
  const { market } = useAppContext();
  const [isHeaderVisible, setHeaderVisible] = useState<boolean>(true);
  const [campaigns, setCampaignsState] = useState([]);
  const dispatch = useDispatch();
  const { product, recommendations } = data;

  const resetStates = () => {
    // needed after a reroute
    dispatch(setSelectedCondition('new'));
  };

  useEffectOnMount(resetStates);

  useEffect(() => {
    if (!IS_BROWSER || !product || !product.id) {
      return;
    }

    fetchDeals(market, product.id).then(
      res =>
        res.hits &&
        res.hits.hits &&
        setCampaignsState(
          res.hits.hits.map((hit: any) => ({
            id: hit._source.campaign.id,
            price: { id: hit._source.priceInfo.priceId.toString() },
          }))
        )
    );
  }, [product && product.id]); // eslint-disable-line

  product.campaigns = campaigns;

  const privacyPolicyUri = market?.privacySettings?.responsive?.privacyUrl;

  const currentCampaign = getCurrentCampaign(
    activeCampaign,
    product
  ) as CurrentCampaign;

  return (
    <PageContent withAds>
      <StyledProductPage>
        {currentModal === PRODUCT_ALERT_MODAL_ID && (
          <Modal
            id={PRODUCT_ALERT_MODAL_ID}
            onRequestClose={hideModal}
            width={500}
          >
            <ProductAlertModalContent
              productId={product.id}
              hideModal={hideModal}
              privacyPolicyUri={privacyPolicyUri || undefined}
            />
          </Modal>
        )}
        <ProductDuringPromoBanner
          product={product}
          campaignBanners={campaignBanners}
        />

        <MobileTopAd />

        <ProductBreadCrumb product={product} />
        <ScrollElement name="Top" />
        <ProductPageHeader
          product={product}
          isHeaderVisible={isHeaderVisible}
          setHeaderVisible={setHeaderVisible}
          currentCampaign={currentCampaign}
        />
        <ProductScrollTopButton />
        <ProductNavigation />
        <div>
          <ProductTabs
            routeSection={routeSection}
            product={product}
            recommendations={recommendations}
            currentCampaign={currentCampaign}
            trendingProducts={product.trendingProducts}
          />

          <Panel className="post-tabs-ad">
            <AdContainer
              placement="panorama_target"
              format="panorama_3_wde"
              min={861}
              max={10000}
              scalable
              scaleBase=".post-tabs-ad"
              triggerPoint={100}
            />
          </Panel>

          <ProductCampaignDeals product={product} />

          <section>
            <Lazy offset={100}>
              <PageSection recessed>
                <RecentlyVisitedProducts offset={1} background />
              </PageSection>
            </Lazy>
          </section>

          <ProductBeforePromoBanner campaignBanners={campaignBanners} />
        </div>
      </StyledProductPage>
    </PageContent>
  );
};
